import { Autorenew } from '@material-ui/icons'
import React, { useState, useReducer, useEffect } from 'react'
import {
  useMutation,
  useNotify,
  useRecordContext,
  useRefresh,
  useShowContext,
} from 'react-admin'
import { ToggleType } from '../../../../core/devices/terminals/terminal-lock-button'
import Button from '../../../common/customized-mui-components/Button'
import { TerminalLockDialog } from '../TerminalDialogs'
import { useHasAuthority } from '../../../hooks/useHasAuthority'
import { Authority } from '../../../../core/auth/Authority'
import { ConfirmDialog } from '../../../common/ConfirmDialog'
import { ResourceName } from '../../../../core/ResourceName'
import {
  terminalLockButtonInitialState,
  terminalLockButtonReducer,
} from '../../../../core/devices/terminals/terminal-lock-button.reducer'
import { TerminalDto } from '../../../../core/dto/device/terminals/terminal.dto'

export const TerminalLockButton = ({ ...props }) => {
  const { asRecurringJob, recurringMethod, ...rest } = props
  const notify = useNotify()
  const hasAuthority = useHasAuthority()
  const { loading } = useShowContext()
  const [{ type, text, mutateQuery }, dispatch] = useReducer(
    terminalLockButtonReducer,
    terminalLockButtonInitialState,
  )
  const [lockDialogOpen, setLockDialogOpen] = useState(false)
  const [disabledId, setDisabledId] = useState(0)
  const [unlockDialogOpen, setUnlockDialogOpen] = useState(false)
  const terminal = useRecordContext<TerminalDto>()
  const [mutate, { loading: mutationLoading }] = useMutation()
  const refresh = useRefresh()

  useEffect(() => {
    if (
      (!asRecurringJob && terminal.disabledId) ||
      (asRecurringJob && recurringMethod === ToggleType.UNLOCK)
    ) {
      dispatch({ type: ToggleType.UNLOCK })
    } else {
      dispatch({ type: ToggleType.LOCK })
    }
  }, [asRecurringJob, terminal, recurringMethod])

  const onClick = async () => {
    if (type === ToggleType.UNLOCK) setUnlockDialogOpen(true)
    else setLockDialogOpen(true)
  }

  const handleLock = async (
    recurringJobName?: string,
    cronExpression?: string,
  ) => {
    let query
    if (!asRecurringJob) {
      query = mutateQuery?.(terminal, disabledId)
    } else {
      query = mutateQuery?.(
        terminal,
        disabledId,
        recurringJobName as string,
        cronExpression as string,
      )
    }
    await mutate(query, {
      returnPromise: true,
      onFailure: (err) => notify(err?.message, 'error'),
    })
    refresh()
  }

  return (
    <>
      <ConfirmDialog
        open={unlockDialogOpen}
        confirm={handleLock}
        source="lock"
        cancel={() => setUnlockDialogOpen(false)}
        action="remove"
        resource={ResourceName.TERMINALS}
        asRecurringJob={asRecurringJob}
      />
      <TerminalLockDialog
        open={lockDialogOpen}
        close={() => setLockDialogOpen(false)}
        disabledId={disabledId}
        setDisabledId={setDisabledId}
        handleLock={handleLock}
        asRecurringJob={asRecurringJob}
      />
      <Button
        variant="contained"
        color="primary"
        label={!asRecurringJob ? text : `${text}-recurring`}
        useSmallVersionBreakpoint={false}
        startIcon={asRecurringJob ? <Autorenew /> : undefined}
        disabled={
          !hasAuthority(Authority.TERMINALS_SET_LOCK) ||
          loading ||
          mutationLoading
        }
        onClick={onClick}
        {...rest}
      />
    </>
  )
}
